import type { ZoneListQuery, ZoneShape } from '@chukua/common';
import { Tag, type TableColumnsType } from 'antd';
import { Link, type LinkProps } from 'react-router';
import { ZoneKindSwatch } from '../../../../../shared/components/ZoneKindSwatch';
import type { ListQueryControls } from '../../../../../shared/hooks/use-list-query';
import { CatalogueRowActions } from '../../../../../shared/components/CatalogueRowActions';
import type { ZoneKind } from '../../../../reference-data/reference-data.api';
import { countryName, formatRadius } from '../../../policies/zone-labels';
import type { ZoneListItem } from '../../../zones.api';

interface BuildZoneColumnsArgs {
  list: Pick<ListQueryControls<ZoneListQuery>, 'sortOrderFor' | 'filteredValueFor'>;
  /** Every kind, inactive ones included: a zone can still carry a kind that has since been retired. */
  zoneKinds: ZoneKind[];
  /** Where a zone's name links: its panel on the map. */
  zoneLink: (zoneId: string) => Pick<LinkProps, 'to' | 'state'>;
  onEdit: (row: ZoneListItem) => void;
  onDeactivate: (row: ZoneListItem) => Promise<void>;
  onReactivate: (row: ZoneListItem) => Promise<void>;
}

const STATUS_FILTERS = [
  { text: 'Active', value: 'active' },
  { text: 'Inactive', value: 'inactive' },
];

const SHAPE_TAGS: Record<ZoneShape, { label: string; color: string }> = {
  polygon: { label: 'Polygon', color: 'success' },
  point: { label: 'Point', color: 'processing' },
  none: { label: 'Not placed', color: 'warning' },
};

function KindCell({ zone, zoneKinds }: { zone: ZoneListItem; zoneKinds: ZoneKind[] }) {
  const color = zoneKinds.find((kind) => kind.code === zone.kind)?.color;
  return (
    <span className="flex items-center gap-2">
      {color && <ZoneKindSwatch color={color} />}
      {zone.kindName ?? zone.kind}
    </span>
  );
}

type RowActionsProps = { zone: ZoneListItem } & Pick<BuildZoneColumnsArgs, 'onEdit' | 'onDeactivate' | 'onReactivate'>;

/** Kept from opening the row, their confirm popups included: React bubbles portal clicks through this span. */
function RowActions({ zone, onEdit, onDeactivate, onReactivate }: RowActionsProps) {
  return (
    <span onClick={(event) => event.stopPropagation()}>
      <CatalogueRowActions row={zone} onEdit={onEdit} onDeactivate={onDeactivate} onReactivate={onReactivate} />
    </span>
  );
}

/** A point zone's tag says how far it reaches: "Point, 3 km". */
function ShapeTag({ zone }: { zone: ZoneListItem }) {
  const { label, color } = SHAPE_TAGS[zone.shape];
  const reach = zone.shape === 'point' && zone.radiusMeters ? `, ${formatRadius(zone.radiusMeters)}` : '';
  return <Tag color={color}>{`${label}${reach}`}</Tag>;
}

/** Sorting and filtering run on the server: each column's `key` is its `sort` field or filter param in `zoneListQuerySchema`. */
export function buildZoneColumns({ list, zoneKinds, zoneLink, onEdit, onDeactivate, onReactivate }: BuildZoneColumnsArgs): TableColumnsType<ZoneListItem> {
  return [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: list.sortOrderFor('name'),
      render: (name: string, zone) => (
        // The row opens the zone too; the link stays for the keyboard and for opening in a new tab.
        <Link className="font-medium text-link hover:underline" {...zoneLink(zone.id)} onClick={(event) => event.stopPropagation()}>
          {name}
        </Link>
      ),
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      sorter: true,
      sortOrder: list.sortOrderFor('code'),
      render: (code: string) => <span className="font-mono text-xs">{code}</span>,
    },
    {
      title: 'Kind',
      key: 'kind',
      sorter: true,
      sortOrder: list.sortOrderFor('kind'),
      filters: zoneKinds.map((kind) => ({ text: kind.name, value: kind.code })),
      filteredValue: list.filteredValueFor('kind'),
      render: (_, zone) => <KindCell zone={zone} zoneKinds={zoneKinds} />,
    },
    {
      title: 'Country',
      key: 'country',
      sorter: true,
      sortOrder: list.sortOrderFor('country'),
      render: (_, zone) => countryName(zone.countryCode) ?? <span className="text-muted">—</span>,
    },
    {
      title: 'Shape',
      key: 'shape',
      render: (_, zone) => <ShapeTag zone={zone} />,
    },
    {
      title: 'Status',
      key: 'status',
      filters: STATUS_FILTERS,
      filteredValue: list.filteredValueFor('status'),
      render: (_, zone) => <Tag color={zone.isActive ? 'success' : 'default'}>{zone.isActive ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: '',
      key: 'actions',
      render: (_, zone) => <RowActions zone={zone} onEdit={onEdit} onDeactivate={onDeactivate} onReactivate={onReactivate} />,
    },
  ];
}
